import { userIsValid, usersAreValid } from "@/helpers/user";
import { useContext, useState, createContext, useEffect } from "react";
import queryString from "query-string";
import { getRandomBoolean } from "@/helpers/boolean";
import { getRandomLastname, getRandomName, getRandomRut } from "@/helpers/example";
import { usePage } from "@inertiajs/inertia-react";

const UsersRegistrationContext = createContext({
    usersRegistration: [],
    responsibleUser: {},
    isAutocompleteResponsible: false,
    setUsersRegistration: (users) => {},
    setResponsibleUser: (user) => {},
    setIsAutocompleteResponsible: (bool) => {},
    handleChangeUsersRegistration: (event, index, options = {name: undefined, value: undefined, extra: []}) => {},
    handleChangeResponsibleUser: (event, options = {name: undefined, value: undefined}) => {},
    removeUsersRegistration: () => {},
    refreshDataUsersRegistration: ({ quantity }) => {},
    selectAll: () => {},
    deselectAll: () => {},
    responsibleForAssistants: false,
    setResponsibleForAssistants: (bool) => {}
});

export const useUsersRegistrationContext = () => useContext(UsersRegistrationContext);

export const UsersRegistrationProvider = ({ children, ...props }) => {
    const sessionStorageKey = 'order-users-registration';

    const getIfResponsibleForAssistants = () => {
        try {
            const sessionStorageData = JSON.parse(window.sessionStorage.getItem(sessionStorageKey));

            if (sessionStorageData.is_responsible_for_assistants) {
                return sessionStorageData.is_responsible_for_assistants;
            }

            return false;
        } catch (error) {
            return false;
        }
    }

    const [responsibleForAssistants, setResponsibleForAssistants] = useState(getIfResponsibleForAssistants());

    const getUsersFromSessionStorage = (quantity) => {
        try {
            const sessionStorageData = JSON.parse(window.sessionStorage.getItem(sessionStorageKey));

            if (!usersAreValid(sessionStorageData.users, quantity, responsibleForAssistants)) {
                return null;
            }

            return sessionStorageData.users;
        } catch (error) {
            return null;
        }
    }

    const getResponsibleUserFromSessionStorage = () => {
        try {
            const sessionStorageData = JSON.parse(window.sessionStorage.getItem(sessionStorageKey));

            if (!userIsValid(sessionStorageData.responsible_user)) {
                return null;
            }

            return sessionStorageData.responsible_user;
        } catch (error) {
            return null;
        }
    }

    const getDefaultUsers = (quantity) => {
        try {
            const defaultValues = {
                name: '',
                lastname: '',
                rut: '',
                assumed_guest: true
            }

            return [...Array(quantity || 1)].map((_, index) => ({
                id: index + 1,
                ...defaultValues
            }));
        } catch (error) {
            return null;
        }
    }

    const getDefaultResponsibleUser = () => {
        const defaultValues = {
            name: '',
            lastname: '',
            rut: '',
            email: '',
            telephone: ''
        }

        return defaultValues;
    }

    const getExampleUsers = (quantity) => {
        try {
            if (props.initialPage.props?.ENABLE_EXAMPLE_USERS) {
                const exampleUsers = [...Array(quantity || 1)].map((_, index) => {
                    const isAssumedGuest = getRandomBoolean();

                    return {
                        id: index + 1,
                        name: getRandomName(),
                        lastname: getRandomLastname(),
                        rut: isAssumedGuest ? '' : getRandomRut(),
                        assumed_guest: isAssumedGuest
                    }
                });

                return exampleUsers;
            }

            return null
        } catch (error) {
            console.log(error)
            return null
        }
    }

    const getUsers = (quantity) => {
        return getUsersFromSessionStorage(quantity) || getExampleUsers(quantity) || getDefaultUsers(quantity) || []
    }

    const parsedQueryString = queryString.parse(window.location.search);

    const [users, setUsers] = useState(getUsers(parseInt(parsedQueryString?.quantity || props.initialPage.props?.quantity)));
    const [responsibleUser, setResponsibleUser] = useState(getResponsibleUserFromSessionStorage() || getDefaultResponsibleUser());

    const [isAutocompleteResponsible, setIsAutocompleteResponsible] = useState(false);
    const [isSelectedAll, setIsSelectedAll] = useState(false);

    useEffect(() => {
        const filteredUsers = users.filter(({ id }) => id !== 1);
        if (isAutocompleteResponsible) {
            setUsersWithSessionStorage([
                {
                    id: 1,
                    name: responsibleUser.name,
                    lastname: responsibleUser.lastname,
                    rut: responsibleUser.rut,
                    assumed_guest: false
                },
                ...filteredUsers
            ])
        } else {
            if (!isSelectedAll) {
                setUsersWithSessionStorage([
                    {
                        ...users.find(({ id }) => id === 1),
                        id: 1,
                        rut: '',
                        assumed_guest: true
                    },
                    ...filteredUsers
                ])
            }
        }
    }, [responsibleUser, isAutocompleteResponsible])

    useEffect(() => {
        if (users.length > 0) {
            setUsersWithSessionStorage(users);
        }

        if (responsibleUser) {
            setResponsibleUserWithSessionStorage(responsibleUser)
        }
    }, [])

    const removeUsers = () => {
        setTimeout(() => {
            setUsers([]);
            window.sessionStorage.removeItem(sessionStorageKey);
        }, 500)
    }

    const setUsersWithSessionStorage = (users) => {
        setUsers(users);
        const dataStringified = JSON.stringify({
            responsible_user: responsibleUser,
            users,
            is_responsible_for_assistants: responsibleForAssistants
        });
        window.sessionStorage.setItem(sessionStorageKey, dataStringified);
    }

    const setResponsibleUserWithSessionStorage = (user) => {
        setResponsibleUser(user);
        const dataStringified = JSON.stringify({
            responsible_user: user,
            users,
            is_responsible_for_assistants: responsibleForAssistants
        });
        window.sessionStorage.setItem(sessionStorageKey, dataStringified);
    }

    const setResponsibleForAssistantsWithSessionStorage = (value) => {
        setResponsibleForAssistants(value);
        const dataStringified = JSON.stringify({
            responsible_user: responsibleUser,
            users,
            is_responsible_for_assistants: value
        });
        window.sessionStorage.setItem(sessionStorageKey, dataStringified);
    }

    const handleChange = (event, index, options = {name: undefined, value: undefined, extra: []}) => {
        const id = index + 1;
        const user = users.find(user => user.id === id);
        const filteredUsers = users.filter(user => user.id !== id);

        const propName = options?.name !== undefined ? options?.name : event.target.name;

        const values = {
            'checkbox': Boolean(event.target.checked),
            'number': parseInt(event.target.value),
            'text': event.target.value,
            'default': event.target.value
        };

        const propValue = options?.value !== undefined ? options?.value : (values[event.target.type] || values['default']);

        const defaultValues = {
            id,
            name: '',
            rut: '',
            assumed_guest: true
        }

        const newUser = {
            ...defaultValues,
            ...user,
            [propName]: propValue,
        }

        if (options?.extra) {
            options?.extra.forEach(({ name, value }) => {
                newUser[name] = value;
            })
        }

        const newUsers = [
            ...filteredUsers,
            newUser
        ]
        .sort((a, b) => {
            if (a.id > b.id) {
                return 1;
            }

            if (a.id < b.id) {
                return -1
            }

            return 0;
        });

        setUsersWithSessionStorage(newUsers);
    }

    const handleChangeResponsibleUser = (event, options = {name: undefined, value: undefined}) => {
        const values = {
            'checkbox': Boolean(event.target.checked),
            'number': parseInt(event.target.value),
            'text': event.target.value,
            'default': event.target.value
        };

        const propName = options?.name !== undefined ? options?.name : event.target.name;
        const propValue = options?.value !== undefined ? options?.value : (values[event.target.type] || values['default']);

        const data = {
            ...responsibleUser,
            [propName]: propValue,
        }

        setResponsibleUserWithSessionStorage(data)
    }

    const refreshData = ({ quantity }) => {
        setUsersWithSessionStorage(getUsers(quantity));
    }

    const selectAll = () => {
        setIsSelectedAll(true);

        setUsersWithSessionStorage(users.map(user => ({
            ...user,
            assumed_guest: false,
        })));
    }

    const deselectAll = () => {
        setIsSelectedAll(false);

        setUsersWithSessionStorage(users.map(user => ({
            ...user,
            rut: user.id === 1 && isAutocompleteResponsible ? responsibleUser.rut : '',
            assumed_guest: user.id === 1 && isAutocompleteResponsible ? false : true,
        })));
    }

    return (
        <UsersRegistrationContext.Provider value={{
            usersRegistration: users,
            responsibleUser,
            isAutocompleteResponsible,

            setUsersRegistration: setUsersWithSessionStorage,
            setResponsibleUser: setResponsibleUserWithSessionStorage,
            setIsAutocompleteResponsible,

            handleChangeUsersRegistration: handleChange,
            handleChangeResponsibleUser,

            removeUsersRegistration: removeUsers,
            refreshDataUsersRegistration: refreshData,

            selectAll,
            deselectAll,

            responsibleForAssistants,
            setResponsibleForAssistants: setResponsibleForAssistantsWithSessionStorage
        }}>
            {children}
        </UsersRegistrationContext.Provider>
    )
}

export default UsersRegistrationContext;
